import { useCallback, useEffect, useMemo, useState } from "react";
import { invokeAdmin, errMsg } from "../lib/adminApi";
import { Banner, CopyId, SortState, Table, Td } from "../components/ui";

type OrganizerProps = {
  name?: string;
  type?: string;
  description?: string;
  calendarUrl?: string;
  [key: string]: unknown;
};

type Organizer = {
  id: string;
  props: OrganizerProps | null;
  type: string | null;
};

const PAGE = 200;

function truncate(s: string, max = 80): string {
  return s.length > max ? `${s.slice(0, max)}…` : s;
}

export default function OrganizersPage() {
  const [q, setQ] = useState("");
  const [organizers, setOrganizers] = useState<Organizer[]>([]);
  const [total, setTotal] = useState<number | null>(null);
  const [offset, setOffset] = useState(0);
  const [hasMore, setHasMore] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [names, setNames] = useState<Record<string, string>>({});
  const [saveLoading, setSaveLoading] = useState<string | null>(null);
  const [sort, setSort] = useState<SortState>({ key: "name", dir: "asc" });

  const fetchOrganizers = useCallback(async (newOffset = 0, append = false) => {
    setLoading(true);
    setError(null);
    try {
      const data = await invokeAdmin<{ organizers: Organizer[]; total?: number }>(
        "admin_organizers_list",
        { limit: PAGE, offset: newOffset },
      );
      setOrganizers((prev) => (append ? [...prev, ...data.organizers] : data.organizers));
      setNames((prev) => {
        const next = append ? { ...prev } : {};
        for (const o of data.organizers) next[o.id] = o.props?.name ?? "";
        return next;
      });
      setTotal(data.total ?? null);
      setOffset(newOffset + data.organizers.length);
      setHasMore(data.organizers.length === PAGE);
    } catch (e) {
      setError(errMsg(e));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect -- mount fetch; loader's sync setLoading/setError reset is intentional (shared with Refresh); data state is only written after the awaited request
    fetchOrganizers(0, false);
  }, [fetchOrganizers]);

  async function rename(id: string) {
    const name = (names[id] ?? "").trim();
    if (!name) {
      setError("Name must not be empty.");
      return;
    }
    setSaveLoading(id);
    setError(null);
    try {
      await invokeAdmin("admin_organizer_update", { id, name });
      await fetchOrganizers(0, false);
    } catch (e) {
      setError(errMsg(e));
    } finally {
      setSaveLoading(null);
    }
  }

  function toggleSort(key: string) {
    setSort((s) => (s.key === key ? { key, dir: s.dir === "asc" ? "desc" : "asc" } : { key, dir: "asc" }));
  }

  // The edge fn has no server-side name filter, so filtering is client-side over
  // the loaded pages (Load-more pulls more) — noted so it isn't mistaken for a
  // full-corpus search.
  const query = q.trim().toLowerCase();
  const filtered = useMemo(() => {
    const rows = query
      ? organizers.filter((o) => (o.props?.name ?? "").toLowerCase().includes(query))
      : organizers.slice();
    const dir = sort.dir === "asc" ? 1 : -1;
    const val = (o: Organizer) =>
      sort.key === "type"
        ? (o.props?.type ?? o.type ?? "").toLowerCase()
        : (o.props?.name ?? "").toLowerCase();
    return rows.sort((a, b) => val(a).localeCompare(val(b)) * dir);
  }, [organizers, query, sort]);

  return (
    <div>
      <h1 className="text-xl font-semibold">Organizers</h1>

      <div className="mt-4 flex gap-2">
        <input
          type="text"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Filter loaded rows by name…"
          className="flex-1 rounded border border-zinc-700 bg-zinc-800 px-3 py-1.5 text-sm text-white placeholder-zinc-500 focus:outline-none focus:ring-1 focus:ring-zinc-500"
        />
        <button
          onClick={() => fetchOrganizers(0, false)}
          disabled={loading}
          className="rounded bg-zinc-700 px-3 py-1.5 text-sm text-white hover:bg-zinc-600 disabled:opacity-50"
        >
          {loading ? "Loading…" : "Refresh"}
        </button>
      </div>

      <div className="mt-2 text-xs text-zinc-500">
        showing {filtered.length}
        {query ? ` filtered / ${organizers.length} loaded` : ` of ${total ?? organizers.length}`}
      </div>

      {error && <Banner className="mt-3">{error}</Banner>}

      <div className="mt-4 overflow-x-auto rounded-lg border border-zinc-800">
        <Table
          head={[
            { label: "Name", sortKey: "name" },
            { label: "Type", sortKey: "type" },
            "Description",
            "ID",
            "Calendar",
            "",
          ]}
          sort={sort}
          onSort={toggleSort}
        >
          {filtered.map((o) => (
            <tr key={o.id} className="border-t border-zinc-800 align-top">
              <Td className="text-zinc-300">{o.props?.name ?? "—"}</Td>
              <Td className="text-zinc-400 text-xs">{o.props?.type ?? o.type ?? "—"}</Td>
              <Td className="text-zinc-400 text-xs max-w-xs">
                {o.props?.description ? (
                  <span className="whitespace-pre-wrap break-words">
                    {truncate(o.props.description)}
                  </span>
                ) : (
                  "—"
                )}
              </Td>
              <Td>
                <CopyId id={o.id} chars={12} />
              </Td>
              <Td className="text-xs">
                {o.props?.calendarUrl ? (
                  <span className="rounded bg-zinc-800 px-1.5 py-0.5 text-zinc-300">📅 calendar</span>
                ) : (
                  <span className="text-zinc-600">—</span>
                )}
              </Td>
              <Td>
                <div className="flex items-center gap-2">
                  <input
                    type="text"
                    value={names[o.id] ?? ""}
                    onChange={(e) => setNames((prev) => ({ ...prev, [o.id]: e.target.value }))}
                    placeholder="Rename…"
                    className="w-40 rounded border border-zinc-700 bg-zinc-800 px-2 py-1 text-xs text-white placeholder-zinc-500 focus:outline-none focus:ring-1 focus:ring-zinc-500"
                    onKeyDown={(e) => e.key === "Enter" && rename(o.id)}
                  />
                  <button
                    onClick={() => rename(o.id)}
                    disabled={saveLoading === o.id}
                    className="rounded bg-zinc-700 px-2 py-0.5 text-xs text-white hover:bg-zinc-600 disabled:opacity-50"
                  >
                    {saveLoading === o.id ? "…" : "Save"}
                  </button>
                </div>
              </Td>
            </tr>
          ))}
          {filtered.length === 0 && !loading && (
            <tr>
              <td colSpan={6} className="px-3 py-4 text-center text-sm text-zinc-500">
                No organizers found.
              </td>
            </tr>
          )}
          {loading && organizers.length === 0 && (
            <tr>
              <td colSpan={6} className="px-3 py-4 text-center text-sm text-zinc-500">
                Loading…
              </td>
            </tr>
          )}
        </Table>
      </div>

      {hasMore && !query && (
        <button
          onClick={() => fetchOrganizers(offset, true)}
          disabled={loading}
          className="mt-3 rounded bg-zinc-700 px-3 py-1.5 text-sm text-white hover:bg-zinc-600 disabled:opacity-50"
        >
          {loading ? "Loading…" : "Load more"}
        </button>
      )}
    </div>
  );
}
